import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { useUpdatePaymentStatus } from '@/hooks/usePayments'
import { getAllowedPaymentStatusOptions } from '@/lib/payments'
import { cn } from '@/lib/utils'
import {
  PAYMENT_FAILURE_REASON_LABELS,
  PAYMENT_STATUS_LABELS,
  type PaymentFailureReason,
  type PaymentStatus,
} from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  paymentId: string
  jobId?: string | null
  currentStatus: PaymentStatus
  currentFailureReason?: PaymentFailureReason | null
  onUpdated?: () => void
}

const FAILURE_REASONS: PaymentFailureReason[] = [
  'not_received_by_due_date',
  'partial_payment',
  'invoice_disputed',
  'system_behavior',
  'stripe_payment_failed',
]

const selectFieldClass =
  'h-10 rounded-xl border-transparent bg-[#f5f6fa] py-0 pl-4 pr-3 text-sm font-normal text-[171a20] data-[placeholder]:text-[171a20] [&>span]:font-normal [&>svg]:text-[171a20] [&>svg]:opacity-100'

const textareaFieldClass =
  'min-h-[120px] resize-none rounded-xl border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal leading-[1.5] text-[#171a20] placeholder:font-normal placeholder:text-[#6c7285]'

const labelClass =
  'inline-flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'

const dialogContentClass =
  'max-h-[90vh] w-[calc(100%-2rem)] max-w-[480px] gap-0 overflow-x-hidden overflow-y-auto border-[#e7e9ef] bg-white p-0 shadow-[0_1px_2px_-1px_rgba(38,45,66,0.05),0_8px_8px_-1px_rgba(38,45,66,0.1)] sm:rounded-2xl *:min-w-0'

const cancelButtonClass =
  'h-11 rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8f9fb]'

const submitButtonClass =
  'h-11 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'

// 2.7.7 Update payment status popup. Pre-set to the current status.
export function UpdatePaymentStatusDialog({
  open,
  onOpenChange,
  paymentId,
  jobId,
  currentStatus,
  currentFailureReason,
  onUpdated,
}: Props) {
  const { t } = useTranslation('payments')
  const [status, setStatus] = useState<PaymentStatus | ''>(currentStatus)
  const [failureReason, setFailureReason] = useState<PaymentFailureReason | ''>(
    ''
  )
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const updateStatus = useUpdatePaymentStatus(paymentId, { jobId })
  const isRefunded = currentStatus === 'refunded'
  const statusOptions = getAllowedPaymentStatusOptions(currentStatus).map(
    statusOption => ({
      value: statusOption,
      label: PAYMENT_STATUS_LABELS[statusOption],
    })
  )

  useEffect(() => {
    if (open) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setStatus(currentStatus)

      setFailureReason(currentFailureReason ?? '')
      setNotes('')
      setError(null)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open, currentStatus, currentFailureReason])

  const submit = async () => {
    if (!status) {
      setError(t('updateStatusDialog.selectStatusError'))
      return
    }
    // On Failed, a sub-reason is mandatory (feedback #26).
    if (status === 'failed' && !failureReason) {
      setError(t('updateStatusDialog.selectFailureReasonError'))
      return
    }
    try {
      await updateStatus.mutateAsync({
        status,
        ...(status === 'failed' && failureReason
          ? { failure_reason: failureReason }
          : {}),
        ...(notes.trim() ? { note: notes.trim() } : {}),
      })
      toast({ variant: 'success', title: t('updateStatusDialog.updated') })
      onUpdated?.()
      onOpenChange(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('updateStatusDialog.updateError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={dialogContentClass}
        overlayClassName='bg-black/70 backdrop-blur-[2px]'
        showCloseButton={false}
      >
        <div className='flex flex-col gap-6 p-8'>
          <DialogHeader className='text-left'>
            <DialogTitle className='font-heading text-base font-semibold leading-[1.2] tracking-normal text-[#171a20]'>
              {t('updateStatusDialog.title')}
            </DialogTitle>
          </DialogHeader>

          <div className='space-y-1'>
            <Label className={labelClass}>
              {t('updateStatusDialog.newStatus')}
              <span className='text-[#d63b33]'>*</span>
            </Label>
            <Select
              value={status}
              disabled={isRefunded}
              onValueChange={v => {
                setStatus(v as PaymentStatus)
                if (v !== 'failed') setFailureReason('')
                setError(null)
              }}
            >
              <SelectTrigger
                className={cn(
                  selectFieldClass,
                  error && 'border border-destructive bg-white'
                )}
                disabled={isRefunded}
              >
                <SelectValue
                  placeholder={t('updateStatusDialog.selectStatus')}
                />
              </SelectTrigger>
              <SelectContent className='rounded-xl border-[#e7e9ef]'>
                {statusOptions.map(o => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    className='rounded-lg py-2.5 pl-8 pr-3 text-sm font-normal text-[#171a20]'
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className='text-xs text-destructive'>{error}</p>}
          </div>
          {status === 'failed' && (
            <div className='space-y-1'>
              <Label className={labelClass}>
                {t('updateStatusDialog.failureReason')}
                <span className='text-[#d63b33]'>*</span>
              </Label>
              <Select
                value={failureReason || undefined}
                onValueChange={v => {
                  setFailureReason(v as PaymentFailureReason)
                  setError(null)
                }}
              >
                <SelectTrigger className={selectFieldClass}>
                  <SelectValue
                    placeholder={t('updateStatusDialog.selectFailureReason')}
                  />
                </SelectTrigger>
                <SelectContent className='rounded-xl border-[#e7e9ef]'>
                  {FAILURE_REASONS.map(r => (
                    <SelectItem
                      key={r}
                      value={r}
                      className='rounded-lg py-2.5 pl-8 pr-3 text-sm font-normal text-[#171a20]'
                    >
                      {PAYMENT_FAILURE_REASON_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className='space-y-1'>
            <Label htmlFor='status-notes' className={labelClass}>
              {t('updateStatusDialog.notes')}
            </Label>
            <Textarea
              id='status-notes'
              value={notes}
              maxLength={2000}
              rows={6}
              className={textareaFieldClass}
              placeholder={t('updateStatusDialog.notesPlaceholder')}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          <DialogFooter className='flex-row justify-end gap-4 space-x-0'>
            <Button
              type='button'
              variant='outline'
              className={cancelButtonClass}
              onClick={() => onOpenChange(false)}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type='button'
              className={submitButtonClass}
              disabled={updateStatus.isPending}
              onClick={submit}
            >
              {t('common:actions.update')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UpdatePaymentStatusDialog
